import { useEffect, useState } from 'react'

/** The report's closing card carries this, so the sticky bar can find the thing it stands
 *  in for without the page having to hold the wiring between them. */
export const REPORT_END_CARD_ID = 'compliance-report-end-card'

const supportsObserver = (): boolean =>
  typeof IntersectionObserver === 'function'

/**
 * Whether the report's closing card is currently on screen.
 *
 * Written for the mobile CTA bar, which stands in for that card while it is still below
 * the fold and stands down once the reader has actually reached it — two copies of the
 * same offer on screen at once reads as a mistake.
 *
 * Reactive rather than one-shot: scrolling back up puts the card away again and brings the
 * bar back, which is what a reader who scrolls past and returns expects. The viewport is
 * the root, which is correct here because the app shell's `main` scroller fills it.
 *
 * Owned by the bar rather than by the page. Held at the page root, every crossing of the
 * card's boundary re-rendered the whole report — every area card and every obligation row
 * — for a boolean only the bar reads.
 *
 * Seeded from whether the observer exists at all, which keeps two properties that pull in
 * opposite directions. With an observer, `true`: its first observation lands after paint,
 * so seeding `false` made a short report — one whose closing card is already on screen —
 * paint the bar and immediately hide it, a flash on every load for the practices with the
 * fewest obligations. Without one, `false`, so the CTA is kept rather than lost to a
 * browser that can never correct the guess.
 */
export const useReachedEndCard = (): boolean => {
  const [reached, setReached] = useState(supportsObserver)

  useEffect(() => {
    if (!supportsObserver()) return undefined
    const el = document.getElementById(REPORT_END_CARD_ID)
    // The card and this bar are gated on the same `endCardSlot === 'rail'`, so they mount
    // in one commit and the element is always here by the time this effect runs. Missing
    // means the slot changed, and the bar is on its way out with it.
    if (!el) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        // The last entry, not the first: the observer keeps a delivery queue, so one
        // target can arrive with several entries in a single callback when changes
        // outpace delivery — which is what fast scrolling produces. Reading `entries[0]`
        // there takes the stale one and leaves the bar wrong until the next crossing.
        setReached(entries[entries.length - 1].isIntersecting)
      },
      // Any sliver counts. The card is taller than a phone's viewport, so waiting for a
      // threshold above zero would keep the bar up over the top of the card it duplicates.
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return reached
}
